/*
 * Copyright 2026 Sony Corporation
 */
import type { LogEntry } from '@ips-jit/schema';
import { useEffect, useState } from 'react';

import { useAuth } from '../contexts/auth-context';

const describe = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback);

const POLL_MS = 5_000;
const PAGE_SIZE = 100;
const MAX_ENTRIES = 300;

/** What the app on the user's device reports (errors, or everything), fetched as it arrives. */
export const useDeviceLog = (projectId: string, errorsOnly: boolean) => {
  const { api } = useAuth();
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    let cursor: string | undefined;
    let polling = false;
    setEntries([]);

    const poll = async () => {
      if (polling) {
        return;
      }
      polling = true;
      try {
        const page = await api.listLogs(projectId, { cursor, errorsOnly, limit: PAGE_SIZE });
        if (cancelled) {
          return;
        }
        cursor = page.cursor ?? cursor;
        if (page.entries.length > 0) {
          setEntries((previous) => [...previous, ...page.entries].slice(-MAX_ENTRIES));
        }
        setError(undefined);
      } catch (caught) {
        if (!cancelled) {
          setError(describe(caught, 'Could not load the device log.'));
        }
      } finally {
        polling = false;
      }
    };

    void poll();
    const timer = setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [api, projectId, errorsOnly]);

  return { entries, error };
};
